interface ISpectraPeak {
  index: number;
  value: number;
  noise: number;
  correct: number;
}

const MAX_PEAK_SEARCH_SIZE = 25;
const LEAK_LINES = 3;
const EPSILON = 1e-6;

export const calcLeakagePower = (peakDistance: number, leakLines: number): number => {
  let distance = peakDistance;
  let power = 0;

  for (let index = 0; index < leakLines; index += 1) {
    let leakage: number;
    if (distance < 1e-5) {
      leakage = 1;
    } else if (distance > 0.99999 && distance < 1.00001) {
      leakage = 0.5;
    } else {
      const angle = Math.PI * distance;
      leakage = Math.sin(angle) / angle;
      const divisor = 1 - distance * distance;
      if (Math.abs(divisor) <= EPSILON) {
        return 0;
      }
      leakage /= divisor;
    }

    power += leakage * leakage;
    distance += 1;
  }

  return Math.sqrt(power);
};

export const findSpectraPeak = (
  spectra: readonly number[],
  searchFrom: number,
  searchSize: number,
): ISpectraPeak | null => {
  const size = spectra.length;
  if (size < 5 || searchFrom < 0 || searchFrom >= size || searchSize < 0) {
    return null;
  }

  let actualSearchSize = Math.min(searchSize + 1, MAX_PEAK_SEARCH_SIZE);
  if (searchFrom + actualSearchSize >= size) {
    actualSearchSize = size - searchFrom - 1;
  }
  if (actualSearchSize < 3) {
    return null;
  }

  const buffer = spectra.slice(searchFrom, searchFrom + actualSearchSize);
  let peakOffset = 0;
  let maxValue = 0;

  for (let index = 1; index < actualSearchSize - 1; index += 1) {
    if (buffer[index - 1] < buffer[index] && buffer[index + 1] < buffer[index] && maxValue < buffer[index]) {
      maxValue = buffer[index];
      peakOffset = index;
    }
  }

  if (peakOffset === 0) {
    return null;
  }

  let value = buffer[(actualSearchSize - 1) >> 1];
  let noise = 0;
  let correct = 0;
  const center = buffer[peakOffset];
  const dInitial = (buffer[peakOffset - 1] - buffer[peakOffset + 1]) / center;

  if (dInitial * dInitial < 1e-5) {
    noise = buffer[peakOffset - 1] + buffer[peakOffset + 1] - center;
    value = center - noise;
  } else {
    if (Math.abs(dInitial) <= EPSILON) {
      return null;
    }

    const initialK = (0.75 * (2 - (buffer[peakOffset - 1] + buffer[peakOffset + 1]) / center)) / dInitial;
    const root = Math.sqrt(initialK * initialK + 1);
    const d = initialK >= 0 ? root - initialK : -root - initialK;
    const angle = Math.PI * d;
    if (Math.abs(angle) <= EPSILON) {
      return null;
    }

    const k = angle / Math.sin(angle);
    correct = d;
    if (d * d > 0.24999 && d * d < 0.25001) {
      value = 1.178097 * center;
    } else {
      const divisor = 1 - 2 * d;
      if (Math.abs(divisor) <= EPSILON) {
        return null;
      }
      value = (k * (1 - d * d) * (2 - d) * (center - buffer[peakOffset - 1])) / divisor;
    }

    const divisor = 1 - d * d;
    if (Math.abs(divisor) <= EPSILON || Math.abs(k) <= EPSILON) {
      return null;
    }
    noise = center - value / divisor / k;
  }

  if (value + noise < center) {
    return null;
  }

  return {
    index: peakOffset + searchFrom,
    value,
    noise,
    correct,
  };
};

export const calcSpectraBandPower = (
  spectra: readonly number[],
  line0: number,
  line1: number,
  isHarmonic: boolean,
): number | null => {
  const size = spectra.length;
  if (
    size < 3 ||
    line0 < 0 ||
    line1 < 0 ||
    line0 >= size ||
    line1 >= size ||
    !Number.isFinite(line0) ||
    !Number.isFinite(line1)
  ) {
    return null;
  }
  if (line0 === line1) {
    return 0;
  }

  const lowerLine = Math.min(line0, line1);
  const upperLine = Math.max(line0, line1);
  const firstLine = Math.ceil(lowerLine);
  const lastLine = Math.floor(upperLine);
  let power = 0;

  for (let index = firstLine; index < lastLine; index += 1) {
    power += spectra[index] * spectra[index];
  }

  let searchLine0 = Math.max(firstLine - LEAK_LINES, 0);
  let searchLine1 = Math.min(firstLine + LEAK_LINES, size - 1);
  let searchLinesCount = searchLine1 - searchLine0 + 1;

  while (searchLinesCount > 1) {
    const peak = findSpectraPeak(spectra, searchLine0, searchLinesCount);
    if (!peak) {
      break;
    }

    const peakLine = peak.index - peak.correct;
    let peakDistance = peakLine - lowerLine;
    if (peakDistance < 0) {
      peakDistance = -peakDistance;
    } else {
      peakDistance += 1;
    }

    const leakage = calcLeakagePower(peakDistance, LEAK_LINES) * peak.value;
    power += peakLine < lowerLine ? -(leakage * leakage) : leakage * leakage;

    if (peak.index === searchLine1) {
      break;
    }
    searchLine0 = peak.index + 1;
    searchLinesCount = searchLine1 - searchLine0;
  }

  searchLine0 = Math.max(lastLine - LEAK_LINES, firstLine);
  searchLine1 = Math.min(lastLine + LEAK_LINES, size - 1);
  searchLinesCount = searchLine1 - searchLine0 + 1;

  while (searchLinesCount > 1) {
    const peak = findSpectraPeak(spectra, searchLine0, searchLinesCount);
    if (!peak) {
      break;
    }

    const peakLine = peak.index - peak.correct;
    let peakDistance = peakLine - upperLine;
    if (peakDistance <= 0) {
      peakDistance = 1 - peakDistance;
    }

    const leakage = calcLeakagePower(peakDistance, LEAK_LINES) * peak.value;
    power += peakLine > upperLine ? -(leakage * leakage) : leakage * leakage;

    if (peak.index === searchLine1) {
      break;
    }
    searchLine0 = peak.index + 1;
    searchLinesCount = searchLine1 - searchLine0;
  }

  const adjustment = isHarmonic ? 1 : 1.5;
  return power > 0 ? Math.sqrt(power / adjustment) : 0;
};

export const resolveSpectraLine = (
  xValue: number,
  firstX: number,
  lastX: number,
  spectraSize: number,
): number | null => {
  const span = lastX - firstX;
  if (spectraSize < 2 || !Number.isFinite(xValue) || !Number.isFinite(span) || span === 0) {
    return null;
  }

  const line = ((xValue - firstX) * (spectraSize - 1)) / span;
  const nearestLine = Math.round(line);
  return Math.abs(line - nearestLine) < 1e-7 ? nearestLine : line;
};

export const calcSpectraBandPowerInRange = ({
  spectra,
  markerX,
  cursorX,
  firstX,
  lastX,
  isHarmonic,
}: {
  spectra: readonly number[];
  markerX: number | null | undefined;
  cursorX: number;
  firstX: number;
  lastX: number;
  isHarmonic: boolean;
}): number | null => {
  if (markerX == null || !Number.isFinite(markerX) || !Number.isFinite(cursorX)) {
    return null;
  }
  const min = Math.max(Math.min(markerX, cursorX), Math.min(firstX, lastX));
  const max = Math.min(Math.max(markerX, cursorX), Math.max(firstX, lastX));
  if (min > max) {
    return null;
  }
  const line0 = resolveSpectraLine(min, firstX, lastX, spectra.length);
  const line1 = resolveSpectraLine(max, firstX, lastX, spectra.length);
  return line0 === null || line1 === null ? null : calcSpectraBandPower(spectra, line0, line1, isHarmonic);
};
